import "@/index.css";
import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";

import PublicLayout from "@/pages/public/PublicLayout";
import Landing from "@/pages/public/Landing";
import SchoolSite from "@/pages/public/SchoolSite";
import Admission from "@/pages/public/Admission";
import TrackAdmission from "@/pages/public/TrackAdmission";
import GetApp from "@/pages/public/GetApp";
import PayFees from "@/pages/public/PayFees";
import MockGateway from "@/pages/public/MockGateway";
import PayResult from "@/pages/public/PayResult";

import SuperLayout from "@/pages/super/SuperLayout";
import SuperDashboard from "@/pages/super/SuperDashboard";
import Schools from "@/pages/super/Schools";
import Subscriptions from "@/pages/super/Subscriptions";
import Revenue from "@/pages/super/Revenue";
import SchoolWebsite from "@/pages/super/SchoolWebsite";
import SuperAdmissions from "@/pages/super/SuperAdmissions";
import SiteEditor from "@/pages/super/SiteEditor";
import DataExport from "@/pages/super/DataExport";

import SchoolLayout from "@/pages/school/SchoolLayout";
import SchoolDashboard from "@/pages/school/SchoolDashboard";
import Students from "@/pages/school/Students";
import Admissions from "@/pages/school/Admissions";
import Academics from "@/pages/school/Academics";
import Exams from "@/pages/school/Exams";
import Inventory from "@/pages/school/Inventory";
import PrintSettings from "@/pages/school/PrintSettings";
import Fees from "@/pages/school/Fees";
import Attendance from "@/pages/school/Attendance";
import Homework from "@/pages/school/Homework";
import Staff from "@/pages/school/Staff";
import AcademicYear from "@/pages/school/AcademicYear";
import WhatsAppLog from "@/pages/school/WhatsAppLog";

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    // On navigation, jump back to the top *before* paint — otherwise clicking a
    // footer link leaves the new page scrolled to the bottom and it looks broken.
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" theme="dark" richColors offset={72} />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/s/:code" element={<SchoolSite />} />
            <Route path="/s/:code/admission" element={<Admission />} />
            <Route path="/track" element={<TrackAdmission />} />
            <Route path="/app" element={<GetApp />} />
            <Route path="/s/:code/fees" element={<PayFees />} />
            <Route path="/pay/mock" element={<MockGateway />} />
            <Route path="/pay/result" element={<PayResult />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route path="/super" element={<ProtectedRoute roles={["super_admin"]}><SuperLayout /></ProtectedRoute>}>
            <Route index element={<SuperDashboard />} />
            <Route path="schools" element={<Schools />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="schools/:id/website" element={<SchoolWebsite />} />
            <Route path="admissions" element={<SuperAdmissions />} />
            <Route path="website" element={<SiteEditor />} />
            <Route path="export" element={<DataExport />} />
          </Route>

          <Route path="/school" element={<ProtectedRoute roles={["school_admin", "teacher"]}><SchoolLayout /></ProtectedRoute>}>
            <Route index element={<SchoolDashboard />} />
            <Route path="students" element={<Students />} />
            <Route path="admissions" element={<Admissions />} />
            <Route path="academics" element={<Academics />} />
            <Route path="exams" element={<Exams />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="print-designs" element={<PrintSettings />} />
            <Route path="fees" element={<Fees />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="homework" element={<Homework />} />
            <Route path="staff" element={<Staff />} />
            <Route path="academic-year" element={<AcademicYear />} />
            <Route path="whatsapp" element={<WhatsAppLog />} />
            <Route path="reports" element={<Navigate to="/school/exams" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
